import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { MapPin, Plus, ArrowLeft, Check, X, AlertCircle, Trash2, Lightbulb, Users, GraduationCap, Package, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only — manage geographic regions and approve coach region requests.
 *
 * Two columns: left = curated region list (CRUD), right = pending coach
 * region requests (approve / reject). Both live under /admin/regions.
 */
export default function AdminRegions() {
  const [regions, setRegions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);  // coach-proposed new regions
  const [heatmap, setHeatmap] = useState([]);  // per-region activity counts
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "", parent_region: "", centre_postcode: "", sort_order: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, rq, sg, hm] = await Promise.all([
        api.get("/regions"),  // default returns all (active + archived) for admin view
        api.get("/admin/region-requests"),
        api.get("/admin/region-suggestions?status=pending"),
        api.get("/admin/regions/heatmap").catch(() => ({ data: [] })),
      ]);
      setRegions(r.data || []);
      setRequests(rq.data || []);
      setSuggestions(sg.data || []);
      setHeatmap(hm.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load regions");
    }
  };
  useEffect(() => { load(); }, []);

  const createRegion = async (e) => {
    e?.preventDefault?.();
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    setBusy(true);
    try {
      await api.post("/regions", {
        label: form.label.trim(),
        parent_region: form.parent_region.trim() || null,
        centre_postcode: form.centre_postcode.trim().toUpperCase() || null,
        sort_order: Number(form.sort_order || 0),
      });
      toast.success("Region created");
      setShowForm(false);
      setForm({ label: "", parent_region: "", centre_postcode: "", sort_order: 0 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/regions/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Region archived" : "Region restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteRegion = async (r) => {
    if (!window.confirm(`Archive ${r.label}? Coaches already assigned will keep their access.`)) return;
    try {
      await api.delete(`/regions/${r.id}`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveRequest = async (req) => {
    try {
      await api.post(`/admin/region-requests/${req.request_id}/approve`);
      toast.success(`${req.coach_name} approved for ${req.region_label}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rejectRequest = async (req) => {
    if (!window.confirm(`Reject ${req.coach_name}'s request for ${req.region_label}?`)) return;
    try {
      await api.post(`/admin/region-requests/${req.request_id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveSuggestion = async (s) => {
    if (!window.confirm(`Create "${s.suggested_label}" as a live region? ${s.coach_name} will be auto-approved for it.`)) return;
    try {
      await api.post(`/admin/region-suggestions/${s.id}/approve`);
      toast.success(`Region "${s.suggested_label}" created`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rejectSuggestion = async (s) => {
    if (!window.confirm(`Reject ${s.coach_name}'s suggestion for "${s.suggested_label}"?`)) return;
    try {
      await api.post(`/admin/region-suggestions/${s.id}/reject`);
      toast.success("Suggestion rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Compute per-region intensity based on active students (primary heat signal).
  // Volt (#CCFF00) alpha ramps 0.06 -> 0.45 across the range. Zero-student regions
  // stay flat so admins can spot them at a glance.
  const maxStudents = Math.max(1, ...heatmap.map((h) => h.students || 0));
  const totals = heatmap.reduce(
    (acc, h) => ({
      students: acc.students + (h.students || 0),
      coaches: acc.coaches + (h.coaches || 0),
      offerings: acc.offerings + (h.offerings || 0),
      bookings_30d: acc.bookings_30d + (h.bookings_30d || 0),
    }),
    { students: 0, coaches: 0, offerings: 0, bookings_30d: 0 },
  );
  const activeHeat = heatmap.filter((h) => h.active !== false);
  const deadZones = activeHeat.filter((h) => (h.students || 0) === 0 && (h.coaches || 0) === 0);

  return (
    <div className="space-y-8" data-testid="admin-regions-page">
      <div>
        <Link to="/admin" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-2 mb-3">
          <ArrowLeft className="w-3 h-3" /> Admin overview
        </Link>
        <div className="label-mono mb-2">REGIONS & AREAS</div>
        <h1 className="h-display text-5xl tracking-tighter">Where do you coach?</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Curate the geographic areas coaches can operate in (e.g. North Wales · Holyhead → Colwyn Bay).
          Coaches pick their primary region for free, additional regions need your approval so no one
          spreads themselves too thin.
        </p>
      </div>

      {/* Heatmap — per-region activity so admins can prioritise expansion / archive dead zones */}
      <section className="card-surface p-5 fade-up" data-testid="regions-heatmap">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono">Activity heatmap</div>
          </div>
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
            <span data-testid="totals-students"><span className="text-[#CCFF00]">{totals.students}</span> students</span>
            <span data-testid="totals-coaches"><span className="text-[#CCFF00]">{totals.coaches}</span> coaches</span>
            <span data-testid="totals-offerings"><span className="text-[#CCFF00]">{totals.offerings}</span> offerings</span>
            <span data-testid="totals-bookings"><span className="text-[#CCFF00]">{totals.bookings_30d}</span> bookings · 30d</span>
          </div>
        </div>
        <p className="text-xs text-[#A3A3A3] mb-4 max-w-3xl">
          Sorted hottest first (by student count). {deadZones.length > 0 ? (
            <span data-testid="dead-zone-callout" className="text-[#FF4500]">
              {deadZones.length} active region{deadZones.length === 1 ? "" : "s"} with zero students AND zero coaches — candidates to archive.
            </span>
          ) : (
            <span>No dead zones — every active region has at least one student or coach.</span>
          )}
        </p>

        {heatmap.length === 0 ? (
          <div className="text-sm text-[#A3A3A3] italic">No regions yet. Add one below.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="heatmap-grid">
            {heatmap.map((h) => {
              const intensity = 0.06 + ((h.students || 0) / maxStudents) * 0.39;
              const isDead = h.active && (h.students || 0) === 0 && (h.coaches || 0) === 0;
              return (
                <div
                  key={h.id}
                  className="p-3 border relative"
                  style={{
                    borderColor: isDead ? "#FF4500" : h.active === false ? "#262626" : "#CCFF0033",
                    background: h.active === false
                      ? "transparent"
                      : `rgba(204, 255, 0, ${intensity.toFixed(3)})`,
                  }}
                  data-testid={`heatmap-region-${h.slug}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-sm text-white truncate flex-1">{h.label}</div>
                    {h.active === false && (
                      <span className="label-mono text-[9px] text-[#FF4500]">ARCH</span>
                    )}
                    {isDead && (
                      <span className="label-mono text-[9px] text-[#FF4500]" data-testid={`dead-zone-badge-${h.slug}`}>DEAD</span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider">
                    <div className="flex flex-col items-center" title="Active students with this as home region">
                      <GraduationCap className="w-3 h-3 text-[#CCFF00] mb-0.5" />
                      <span className="text-white text-sm font-semibold" data-testid={`students-${h.slug}`}>{h.students || 0}</span>
                    </div>
                    <div className="flex flex-col items-center" title="Coaches approved for this region">
                      <Users className="w-3 h-3 text-[#CCFF00] mb-0.5" />
                      <span className="text-white text-sm font-semibold" data-testid={`coaches-${h.slug}`}>{h.coaches || 0}</span>
                    </div>
                    <div className="flex flex-col items-center" title="Active offerings in this region">
                      <Package className="w-3 h-3 text-[#CCFF00] mb-0.5" />
                      <span className="text-white text-sm font-semibold" data-testid={`offerings-${h.slug}`}>{h.offerings || 0}</span>
                    </div>
                    <div className="flex flex-col items-center" title="Bookings in last 30 days">
                      <TrendingUp className="w-3 h-3 text-[#CCFF00] mb-0.5" />
                      <span className="text-white text-sm font-semibold" data-testid={`bookings-${h.slug}`}>{h.bookings_30d || 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: regions */}
        <section className="card-surface p-5 fade-up" data-testid="regions-list">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">Regions ({regions.length})</div>
            </div>
            <button onClick={() => setShowForm((s) => !s)} className="btn-volt px-3 py-1.5 text-[10px] inline-flex items-center gap-1" data-testid="new-region-btn">
              <Plus className="w-3 h-3" /> New
            </button>
          </div>

          {showForm && (
            <form onSubmit={createRegion} className="space-y-3 mb-4 p-3 border border-[#262626]" data-testid="region-form">
              <div>
                <div className="label-mono mb-1">Label</div>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. North Wales — Holyhead to Colwyn Bay"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm"
                  data-testid="region-label"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-1">Parent (optional)</div>
                  <input
                    value={form.parent_region}
                    onChange={(e) => setForm({ ...form, parent_region: e.target.value })}
                    placeholder="e.g. North Wales"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm"
                    data-testid="region-parent"
                  />
                </div>
                <div>
                  <div className="label-mono mb-1">Centre postcode</div>
                  <input
                    value={form.centre_postcode}
                    onChange={(e) => setForm({ ...form, centre_postcode: e.target.value })}
                    placeholder="e.g. LL30"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm"
                    data-testid="region-postcode"
                  />
                </div>
              </div>
              <button type="submit" disabled={busy} className="btn-volt px-4 py-2 text-xs" data-testid="region-create-submit">
                Create region
              </button>
            </form>
          )}

          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {regions.length === 0 && (
              <div className="text-sm text-[#A3A3A3] italic">No regions yet. Add your first above.</div>
            )}
            {regions.map((r) => (
              <div key={r.id} className="flex items-start gap-2 p-3 border border-[#262626]" data-testid={`region-row-${r.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm text-white truncate">{r.label}</div>
                    {!r.active && <span className="label-mono text-[#FF4500]">ARCHIVED</span>}
                  </div>
                  <div className="text-[10px] text-[#A3A3A3] mt-0.5">
                    {r.slug} {r.parent_region && `· ${r.parent_region}`} {r.centre_postcode && `· ${r.centre_postcode}`}
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(r)}
                  className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1"
                  data-testid={`region-toggle-${r.slug}`}
                >
                  {r.active ? "Archive" : "Restore"}
                </button>
                {r.active && (
                  <button onClick={() => deleteRegion(r)} className="text-[#A3A3A3] hover:text-[#FF4500] p-1" data-testid={`region-delete-${r.slug}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Right: pending requests */}
        <section className="card-surface p-5 fade-up" data-testid="region-requests-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 text-[#FF4500]" />
            <div className="label-mono">Pending requests ({requests.length})</div>
          </div>
          <p className="text-xs text-[#A3A3A3] mb-3">
            Coaches who already have one approved region asking to add another. Approving lets them list offerings in that area too.
          </p>
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {requests.length === 0 && (
              <div className="text-sm text-[#A3A3A3] italic">No pending requests.</div>
            )}
            {requests.map((req) => (
              <div key={req.request_id} className="p-3 border border-[#262626]" data-testid={`region-req-${req.request_id}`}>
                <div className="text-sm text-white">{req.coach_name}</div>
                <div className="text-[10px] text-[#A3A3A3] mb-2">
                  Wants <span className="text-[#CCFF00]">{req.region_label}</span> · already in {req.current_regions.length} region(s)
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveRequest(req)} className="btn-volt px-3 py-1 text-[10px] inline-flex items-center gap-1" data-testid={`approve-req-${req.request_id}`}>
                    <Check className="w-3 h-3" /> Approve
                  </button>
                  <button onClick={() => rejectRequest(req)} className="px-3 py-1 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-1" data-testid={`reject-req-${req.request_id}`}>
                    <X className="w-3 h-3" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Coach-proposed new regions — full width so admins can scan headers + notes */}
      <section className="card-surface p-5 fade-up" data-testid="region-suggestions-panel">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono">Coach suggestions ({suggestions.length})</div>
        </div>
        <p className="text-xs text-[#A3A3A3] mb-3 max-w-3xl">
          Coaches proposing brand-new areas we don&apos;t cover yet. Approving turns the suggestion into a
          live region and auto-approves the suggesting coach — no need to hand-craft it in the form above.
        </p>
        <div className="space-y-2">
          {suggestions.length === 0 && (
            <div className="text-sm text-[#A3A3A3] italic">No pending suggestions.</div>
          )}
          {suggestions.map((s) => (
            <div key={s.id} className="p-3 border border-[#262626] grid md:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`region-suggestion-${s.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white">{s.suggested_label}</div>
                <div className="text-[10px] text-[#A3A3A3] mt-0.5">
                  from <span className="text-[#CCFF00]">{s.coach_name || s.coach_email}</span>
                  {s.parent_region && <> · parent: {s.parent_region}</>}
                  {s.centre_postcode && <> · centre: {s.centre_postcode}</>}
                </div>
                {s.notes && (
                  <div className="text-xs text-[#A3A3A3] mt-1 italic">&ldquo;{s.notes}&rdquo;</div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => approveSuggestion(s)} className="btn-volt px-3 py-1 text-[10px] inline-flex items-center gap-1" data-testid={`approve-suggestion-${s.id}`}>
                  <Check className="w-3 h-3" /> Create region
                </button>
                <button onClick={() => rejectSuggestion(s)} className="px-3 py-1 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-1" data-testid={`reject-suggestion-${s.id}`}>
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
